import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { environment } from '@env/environment';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation/vertical/vertical.component';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher/media-watcher.service';
import { NotificationsComponent } from '@layout/common/notifications/notifications.component';
import { UserComponent } from '@layout/common/user/user.component';
import { TranslocoModule } from '@ngneat/transloco';
import { FileUploadService } from '@shared/service/fileUpload.service';
import { HoverTrackService } from '@shared/service/hover-track.service';
import { RouteLoadingService } from '@shared/service/route-loading.service';
import { ProgressBarComponent } from '@shared-ui/progress-bar/progress-bar.component';
import { RtisLogoComponent } from '@shared-ui/rtis-logo/rtis-logo.component';
import { CurrentUserQuery } from '@state/auth-state/current-user.query';
import { CurrentUserStore } from '@state/auth-state/current-user.store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FuseVerticalNavigationComponent as FuseVerticalNavigationComponent_1 } from '../../../fuse/components/navigation/vertical/vertical.component';
import { GlassGlowDirective } from '../../../fuse/directives/glass-glow/glass-glow.directive';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dense-layout',
  templateUrl: './dense.component.html',
  styleUrls: ['./dense.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgClass,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    FuseVerticalNavigationComponent_1,
    GlassGlowDirective,
    TranslocoModule,
    ProgressBarComponent,
    RtisLogoComponent,
    NotificationsComponent,
    UserComponent
  ]
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private __activatedRoute = inject(ActivatedRoute);
  private __router = inject(Router);
  private _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _hoverTrackService = inject(HoverTrackService);
  private currentUserQuery = inject(CurrentUserQuery);
  fileUploadService = inject(FileUploadService);
  readonly routeLoadingService = inject(RouteLoadingService);
  private currentUserStore = inject(CurrentUserStore);
  isScreenSmall: boolean;
  navigationAppearance: 'default' | 'dense' = 'default';
  isHover: boolean = false;
  currentRoute: string;
  consultant: any;
  organizationLogo: string;
  organizationName: string;
  organizationType: string;
  organizationNameFirstLetter: any;
  isYearChange: any;
  selectedLanguage = 'en';
  private _unsubscribeAll: Subject<void> = new Subject<void>();
  currentPlan: any = null;
  organizationData: any;
  constructor() {
    this.hoverTrack();
    this.getCurrentUserData();
  }
  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }
  deviceType(): string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    } else if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua
      )
    ) {
      this.isHover = true;
      return 'mobile';
    }
    return 'desktop';
  }
  hoverTrack(): void {
    this._hoverTrackService.hoverTrack.subscribe((res: any) => {
      this.isHover = res;
      this.cdr.markForCheck();
    });
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------
  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
        this.cdr.markForCheck();
      });
  }
  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }
  /**
   * Toggle the navigation appearance
   */
  toggleNavigationAppearance(): void {
    this.navigationAppearance = this.navigationAppearance === 'default' ? 'dense' : 'default';
  }
  private getCurrentUserData(): void {
    this.currentUserQuery
      .select(state => state.data)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((currentUserData: any) => {
        
        if (!currentUserData?.userType) {
          return;
        }
        this.selectedLanguage = currentUserData.language;
        this.organizationType = currentUserData?.userType?.toLowerCase();
        this.organizationData = currentUserData.organization;
        if (currentUserData.viewAs == 'from_admin_to_factory') {
          this.organizationType = 'factory';
        }
        if (currentUserData.viewAs == 'from_admin_to_brand') {
          this.organizationType = 'brand';
        }
        if (currentUserData.viewAs == 'from_admin_to_consultant') {
          this.organizationType = 'consultant';
        }
        const currentUser = currentUserData?.userRole?.toLowerCase();
        if (currentUserData && this.organizationType !== 'admin') {
          if (
            this.organizationType === 'group_of_factory' &&
            currentUser === 'admin' &&
            currentUserData.viewAs === 'factories'
          ) {
            this.organizationLogo = currentUserData?.group?.logo
              ? environment.BASE_URL + currentUserData?.group?.logo
              : null;
            this.organizationName = currentUserData?.group?.name;
            if (currentUserData?.group?.name) {
              this.organizationNameFirstLetter = currentUserData?.group?.name.charAt(0);
            }
          } else {
            this.organizationLogo = currentUserData?.organization?.logo
              ? environment.BASE_URL + currentUserData?.organization?.logo
              : null;
            this.organizationName = currentUserData?.organization?.name;
            if (this.organizationType === 'factory') {
              this.currentPlan =
                currentUserData?.organization?.factorySubscriptionPlan?.subscriptionPlan.name;
            }
            if (currentUserData?.organization?.name) {
              this.organizationNameFirstLetter = currentUserData?.organization?.name.charAt(0);
            }
          }
        } else if (
          currentUser === 'admin' &&
          (currentUserData.viewAs === 'from_admin_to_factory' ||
            currentUserData.viewAs === 'from_admin_to_brand' ||
            currentUserData.viewAs === 'from_admin_to_consultant')
        ) {
          this.organizationLogo = currentUserData?.organization?.logo
            ? environment.BASE_URL + currentUserData?.organization?.logo
            : null;
          this.organizationName = currentUserData?.organization?.name;
          if (currentUserData?.organization?.name) {
            this.organizationNameFirstLetter = currentUserData?.organization?.name.charAt(0);
          }
        } else {
          this.organizationLogo = 'assets/logo/iri-logo.png';
          this.organizationName = 'Resource Tracking Information System';
          this.cdr.markForCheck();
        }
      });
  }
  languageChanged(ev: MatSelectChange) {
    this.currentUserStore.updateOne({
      language: ev.value
    });
  }
}
